'use client';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import sortBy from 'lodash/sortBy';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { TealiumUTag } from 'shared/types/tealium/TealiumUTag';
import { getCookie } from 'typescript-cookie';
import Payment from '@/components/customercenter-ui/checkout/common/payment';
import ReviewStep from '@/components/customercenter-ui/checkout/common/reviewStep';
import CustomerOrderSummary from '@/components/customercenter-ui/components/cart/components/OrderSummary';
import { PageTexts } from '@/components/customercenter-ui/components/cart/i18n';
import { sortNotesByTimestamp } from '@/components/customercenter-ui/components/cart/utils';
import CustomerCartDetails from '@/components/customercenter-ui/components/customer-detail/components/Cart/CartDetails';
import { RESPONSE_SUCCESS_MESSAGE } from '@/components/customercenter-ui/components/customer-detail/constants';
import { triggerTealiumEvent, getPageUtagData } from '@/components/customercenter-ui/utils/tealiumUtils';
import { useCheckout } from '@/context/checkoutProvider';
import { CreditSummaryContext } from '@/context/creditSummaryProvider';
import Header from '@/customercenter-ui/checkout/common/payment/Header';
import ErrorIcon from '@/customercenter-ui/svgs/notifications/error';
import SuccessIcon from '@/customercenter-ui/svgs/notifications/success';
import { useAccount, useCart } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { TranslateText } from '@/helpers/utils/siteUtils';
import { CustomerDetailsPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import GuestShipping from './guestShipping';
import { DEFAULT_SITE_LANG, COOKIE_KEYS } from '../../../constants/constant';
import OrderAdjustments from '../../customercenter-ui/components/cart/components/Adjustments';
import Notes from '../../customercenter-ui/components/cart/components/Notes';
import Notification from '../common/notification/notification';
const CreditSummary = dynamic(
  () => import('../../customercenter-ui/components/customer-detail/components/CreditSummary/creditSummary'),
  {
    ssr: false,
    loading: () => null,
  },
);

export interface CheckoutProps {
  data: any;
}

const Checkout: React.FC<CheckoutProps> = ({ data }) => {
  const {
    currentCheckoutStep,
    notificationMessage,
    setNotificationMsg,
    isError,
    customerDetailsResponse,
    setCustomerDetailsResponse,
    setContextCart,
    contextCart,
  } = useCheckout();
  const {
    customerData,
    setCustomerData,
    setCustomerDetailsResponse: setCreditSummaryDetailsResponse,
  } = useContext(CreditSummaryContext);
  const { getCustomerDetails } = useAccount();
  const customerBootstrapStarted = useRef(false);
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const [siteId, setSiteId] = useState<string>();
  const projectSettings: ProjectSettings = useProjSettings();
  const allowedCreditSites = JSON.parse(projectSettings?.allowedCreditSites ?? 'ZBSite');
  const { data: cartData } = useCart(false);
  const resolvedCart = contextCart ?? cartData;
  const availableShippingMethods = useMemo(
    () => sortBy(resolvedCart?.availableShippingMethods ?? [], ['displayOrder']),
    [resolvedCart?.availableShippingMethods],
  );
  const notes = resolvedCart?.additionalDetails?.notes?.notes ?? [];
  const reasons = resolvedCart?.additionalDetails?.notes?.reasons ?? [];
  const sortedNotes = sortNotesByTimestamp([...notes, ...reasons]);

  useEffect(() => {
    if (cartData) {
      setContextCart(cartData);
    }
  }, [cartData, setContextCart]);

  useEffect(() => {
    const cookieSiteId = getCookie(COOKIE_KEYS.selectedSite) ?? 'ZBSite';
    setSiteId(cookieSiteId);
  }, []);

  useEffect(() => {
    if (customerBootstrapStarted.current) return;
    customerBootstrapStarted.current = true;

    const syncCustomerToCreditSummary = (response: any) => {
      if (response?.Customer) {
        setCustomerData(response.Customer);
        setCreditSummaryDetailsResponse(response);
      }
    };

    const bootstrapCustomerDetails = async () => {
      if (customerDetailsResponse?.code === RESPONSE_SUCCESS_MESSAGE && customerDetailsResponse?.Customer) {
        if (!customerData) {
          syncCustomerToCreditSummary(customerDetailsResponse);
        }
        return;
      }

      if (customerData) {
        return;
      }

      const currentCDPData = getCookie(COOKIE_KEYS.CDP_URL) ?? '';
      if (!currentCDPData) return;

      try {
        const cdpData = JSON.parse(currentCDPData);
        const payload = {
          query: {
            customerId: cdpData?.customerId,
            omsCustomerId: cdpData?.omsCustomerId,
            divisionCode: cdpData?.divisionCode,
            idName: cdpData?.idName ?? 'customerId',
          },
        } as CustomerDetailsPayload;
        const response = await getCustomerDetails(payload);
        if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
          setCustomerDetailsResponse(response);
          syncCustomerToCreditSummary(response);
        }
      } catch {
        // keep checkout usable even if customer prefetch fails
      }
    };

    bootstrapCustomerDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap once on checkout mount
  }, []);

  const renderStepComponent = () => {
    switch (currentCheckoutStep) {
      case 1:
        return (
          <GuestShipping
            data={data}
            onPaymentPage={false}
            cartData={resolvedCart}
            shippingMethods={availableShippingMethods}
          />
        );
      case 2:
        return <Payment data={data} cartData={resolvedCart} availableShippingMethods={availableShippingMethods} />;
      case 3:
        return <ReviewStep data={data} cartData={resolvedCart} availableShippingMethods={availableShippingMethods} />;

      default:
        return null;
    }
  };

  useEffect(() => {
    if (currentCheckoutStep === 2) {
      window.scrollTo({ top: 380, behavior: 'smooth' });
    }
    const pageLinkUtagData: TealiumUTag = getPageUtagData('ShippingPaymentReview', undefined);
    triggerTealiumEvent(pageLinkUtagData);
  }, [currentCheckoutStep]);

  return (
    <section className="max-w-screen-9xl mx-auto md:px-4">
      <section>
        <Header />
      </section>
      <section className="flex flex-col gap-6 md:flex-row  lg:grid lg:grid-cols-5 lg:gap-[56px]">
        <section className="w-full md:max-w-full lg:col-span-3">
          <Notification
            message={notificationMessage}
            isError={isError}
            color={isError ? 'bg-[#FFEBEB]' : 'bg-[#E7F6ED]'}
            textColor={'text-black font-medium'}
            icon={isError ? <ErrorIcon className="h-5 w-5" /> : <SuccessIcon />}
            classname={notificationMessage ? 'block mb-3' : 'hidden'}
            notificationMessage={notificationMessage}
            setNotificationMessage={setNotificationMsg}
            showUndoButton={false}
          />{' '}
          {renderStepComponent()} {/* Render the appropriate component */}
          <Notes
            Texts={Texts}
            notesTableData={sortedNotes}
            className={'my-4 px-5 md:rounded-sm md:border md:border-foggyGray md:shadow-sm md:shadow-foggyGray'}
          ></Notes>
          <OrderAdjustments
            Texts={Texts}
            pageName="checkout"
            totalPrice={data?.subTotal}
            noteNumber={[...notes, ...reasons]?.length}
            className={'my-4 px-5 md:rounded-sm md:border md:border-foggyGray md:shadow-sm md:shadow-foggyGray'}
          />
          {/* <PriorPromos
            Texts={Texts}
            className={'my-4 px-5 md:rounded-sm md:border md:border-foggyGray md:shadow-sm md:shadow-foggyGray'}
          /> */}
        </section>
        <section className="flex w-full flex-col gap-6 px-4 md:max-w-[400px] md:px-0 lg:col-span-2">
          <CustomerCartDetails
            currentCheckoutStep={currentCheckoutStep}
            pageContext="checkout"
            showOrderSummary={false}
          />
          <CustomerOrderSummary widgetData={{ widgetData: data }} Texts={Texts} pageName="checkout" />
          {siteId && allowedCreditSites?.creditSites?.includes(siteId) && <CreditSummary />}
        </section>
      </section>
    </section>
  );
};
export default Checkout;
